import {
  ApiKey,
  CreateKeyRequest,
  CreateKeyResponse,
  KeysClient,
} from '@rhapsody/ngx-api-key-client';
import { Injectable, computed, inject, signal } from '@angular/core';

import { KeyState } from '@core/models';
import { KeyStr } from '@core/constants/create-key.constants';

@Injectable({
  providedIn: 'root',
})
export class KeyService {
  readonly keysClient = inject(KeysClient);

  readonly #state = signal<KeyState>({
    createdKey: null,
    createdKeyError: '',
    byAccount: false,
    customerNumber: '',
    filteredKeys: [],
    listKeysResponse: [],
    showExpiredKeys: false,
    showRequestOptions: true,
  });

  readonly state = computed(() => this.#state());
  readonly createdKey = computed(() => this.#state().createdKey);
  readonly createKeyError = computed(() => this.#state().createdKeyError);
  readonly customerNumber = computed(() => this.#state().customerNumber);
  readonly listKeysResponse = computed(() => this.#state().listKeysResponse);
  readonly filteredKeys = computed(() => this.#state().filteredKeys);
  readonly showExpiredKeys = computed(() => this.#state().showExpiredKeys);
  readonly showRequestOptions = computed(
    () => this.#state().showRequestOptions
  );

  public getKeys(
    byAccount: boolean,
    customerNumber: string,
    showExpiredKeys: boolean | undefined
  ) {
    this.setListKeysResponse('Loading...');
    this.setRequestOptions(false);

    this.keysClient
      .keysGet(
        byAccount ? customerNumber : undefined,
        showExpiredKeys ? true : undefined
      )
      .subscribe({
        next: (res: ApiKey[] | any) => this.setListKeysResponse(res),
        error: (error: any) => this.setListKeysResponse(error),
      });
  }

  public createKey({
    keyName,
    expires,
  }: {
    keyName: string;
    expires: Date | string | null;
  }) {
    const body = {
      name: keyName,
      expires_at: expires ? new Date(expires) : undefined,
    } as CreateKeyRequest;

    this.setCreateKey(null, true, 'Create Key...');

    this.keysClient.keysPost(body).subscribe({
      next: (res: CreateKeyResponse) => this.setCreateKey(res, false),
      error: (err) => this.setCreateKey(null, true, err),
    });
  }

  public patchKey(keyDateChange: { id: string; expires_at: Date }) {
    this.keysClient
      .keysPatch(keyDateChange.id, {
        expires_at: new Date(keyDateChange.expires_at),
      })
      .subscribe({
        next: (res: ApiKey) => this.updateApiKey(res),
        error: (err) => console.error(err),
      });
  }

  public filterKeys(val: string) {
    const { listKeysResponse } = this.#state();
    if (!Array.isArray(listKeysResponse)) return;
    const filteredKeys = listKeysResponse.filter((item) =>
      String(item['name']).toLowerCase().includes(val.toLowerCase())
    );
    this.#state.update((state) => ({ ...state, filteredKeys }));
  }

  private updateApiKey(apiKeyVal: ApiKey) {
    const { listKeysResponse } = this.#state();
    const updateList = Array.isArray(listKeysResponse)
      ? listKeysResponse.map((key) => {
          if (key.id === apiKeyVal.id) {
            return apiKeyVal;
          } else return key;
        })
      : listKeysResponse;

    this.#state.update((state) => ({
      ...state,
      listKeysResponse: updateList,
      filteredKeys: Array.isArray(updateList) ? updateList : [],
    }));
  }

  private setListKeysResponse(keys: ApiKey[] | string) {
    const listKeysResponse = Array.isArray(keys)
      ? keys
      : JSON.stringify(keys, null, 2);

    this.#state.update((state) => ({
      ...state,
      listKeysResponse,
      filteredKeys: Array.isArray(listKeysResponse) ? listKeysResponse : [],
    }));
  }

  private setCreateKey(
    createdKey: ApiKey | null,
    error = false,
    errorRes?: string
  ) {
    if (!error) {
      const mappedCreatedKey = this.mapCreatedKeyProperties(createdKey);
      this.#state.update((state) => ({
        ...state,
        createdKey: mappedCreatedKey,
        createdKeyError: '',
      }));
    } else {
      this.#state.update((state) => ({
        ...state,
        createdKeyError: errorRes ?? '',
      }));
    }

    this.updateListsAfterCreate(createdKey);
  }

  private mapCreatedKeyProperties(createdKey: ApiKey | null): ApiKey {
    const mappedCreatedKey: any = {};
    for (const key in createdKey) {
      if (createdKey.hasOwnProperty(key)) {
        const mappedKey = KeyStr[key as keyof typeof KeyStr] || key;
        mappedCreatedKey[mappedKey] = createdKey[key];
      }
    }
    return mappedCreatedKey;
  }

  private updateListsAfterCreate(createdKey: ApiKey | null) {
    const { listKeysResponse } = this.#state();
    if (Array.isArray(listKeysResponse)) {
      if (typeof createdKey !== 'string' && createdKey !== null) {
        listKeysResponse.push(createdKey);
      }
    }
    const mapList = Array.isArray(listKeysResponse)
      ? listKeysResponse.map((list) => {
          const { key, ...rest } = list;
          return rest;
        })
      : [];

    this.setListKeysResponse(mapList);
  }

  private setRequestOptions(val: boolean) {
    this.#state.update((state) => ({
      ...state,
      showRequestOptions: val,
    }));
  }

  public resetCreatedKey() {
    this.#state.update((state) => ({
      ...state,
      createdKey: null,
      createdKeyError: '',
    }));
  }

  public clearState() {
    this.#state.update(() => ({
      createdKey: null,
      createdKeyError: '',
      byAccount: false,
      customerNumber: '',
      listKeysResponse: '',
      filteredKeys: [],
      showExpiredKeys: false,
      showRequestOptions: true,
    }));
  }
}
